// useMic — microphone capture for STT dictation. Native-only as of
// v0.3.6: capture flows exclusively through the Android `VultureMic`
// JavascriptInterface (Kotlin AudioRecord with VOICE_RECOGNITION +
// system NS/AEC/AGC effects + multi-mic beamforming on devices that
// support it). Browser without the bridge sees `available=false` and
// the UI hides the mic button — there's no getUserMedia fallback
// because the WebRTC path can't discriminate background voices and
// produced bad transcripts in noisy households.
//
// Streaming flow:
//   click mic → calls VultureMic.start() + opens WS to /api/stt/stream
//   native fires window.__onMicFrame(b64) per ~100 ms 16 kHz int16 frame
//     → we ArrayBuffer-decode + ws.send(binary) (same wire format as before)
//   native fires window.__onMicLevel(rms) every ~500 ms with rms in [0,1]
//     → mic UI renders the level bar
//   server emits {"type":"partial","text":...} every ~560 ms
//     → onStreamPartial(text) replaces the running turn in the draft
//   server emits {"type":"vad_stopped"} after 1.5 s of silence
//     → we stop native capture; server is about to send final
//   server emits {"type":"final","text":...}
//     → onStreamFinal(text) commits the turn
//   click again → sends "end" control message → server flushes + closes
//
// Replacement vs append: streaming partials are the FULL transcript
// for this speech turn — the model may revise earlier words as more
// context arrives ("I weigh" → "I went" as the verb gets enough
// rhyme). The composer's responsibility is to remember the draft
// baseline at stream start, and on each partial render the draft as
// `${baseline} ${turnText}`.
//
// uploadFile() still uses the HTTP /api/stt path so file uploads are
// unaffected by the mic-bridge cutover.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";

export type MicState = "idle" | "streaming" | "transcribing" | "uploading";

interface UseMicArgs {
  /** Called when streaming starts. Caller should snapshot its draft
   *  state at this point — subsequent onStreamPartial calls replace
   *  the running turn, not append to it. */
  onStreamStart?: () => void;
  /** Called every ~560 ms during streaming with the full transcript
   *  for the current speech turn. Earlier words may be revised as
   *  the model gets more context. The caller should render the
   *  draft as `${baseline} ${turnText}`. */
  onStreamPartial?: (turnText: string) => void;
  /** Called once when streaming ends with the final turn transcript.
   *  Same shape as onStreamPartial — it's the last partial, after
   *  the server flushes its tail. */
  onStreamFinal?: (turnText: string) => void;
  onError?: (msg: string) => void;
}

export interface UseMic {
  state: MicState;
  /** True only when the native `VultureMic` bridge is present
   *  (i.e. running inside the Android shell). Browsers see false; UI
   *  should hide the mic button entirely. */
  available: boolean;
  /** Current input level in [0, 1] — RMS normalized to int16 peak.
   *  Updated at ~2 Hz during streaming, snaps back to 0 when idle. */
  level: number;
  /** Toggle: idle → start streaming. streaming → stop streaming. */
  toggle: () => Promise<void>;
  /** Hard-cancel an active stream without committing the final
   *  transcript. Used by the "I changed my mind" path. */
  cancel: () => void;
  /** Hand a pre-recorded audio file (mp3, wav, m4a, webm, ogg, …) to
   *  /api/stt and surface the transcript via onStreamFinal. Sized
   *  to multi-hour recordings — the server proxies as a stream and
   *  Parakeet accepts up to 500 MB by default. */
  uploadFile: (file: File) => Promise<void>;
}

interface StreamSession {
  ws: WebSocket;
  /** Most-recent partial text received from the server. */
  lastPartial: string;
  /** Set when the user has asked to stop (or VAD auto-stopped). */
  stopping: boolean;
  /** Set when the user has hit cancel — suppresses the onStreamFinal
   *  callback so the draft doesn't pick up partial text the user
   *  explicitly threw away. */
  cancelled: boolean;
  /** Whether VultureMic.stop() has already been called for this
   *  session. Without this guard, stop() + the eventual ws-close
   *  handler each fire VultureMic.stop(), double-decrementing main's
   *  refcount and tearing down a capture that other consumers
   *  (wake-mic-loop) are still using. */
  micStopped: boolean;
  /** Multi-consumer bridge unsubscribe handles. Null when we used the
   *  legacy window.__onMicFrame global instead. */
  unsubscribeFrame?: () => void;
  unsubscribeLevel?: () => void;
}

// Native bridge — exposed by chat-android/MicBridge.kt OR by
// chat-kitchen/electron/preload.js. Methods are synchronous (the
// Android JNI boundary requires it; the kitchen preload mirrors the
// contract for source compatibility).
//
// _addFrameHandler / _addLevelHandler are kitchen-only multi-consumer
// extensions — Android still pushes frames via the window.__onMicFrame
// global. useMic prefers _addFrameHandler when present so it can
// share frames with wake-mic-loop without either trampling the other.
interface VultureMicBridge {
  isAvailable: () => string;
  start: () => string;
  stop: () => string;
  _addFrameHandler?: (fn: (b64: string) => void) => () => void;
  _addLevelHandler?: (fn: (rms: number) => void) => () => void;
}
interface VultureTtsBridge {
  isAvailable: () => string;
  speak: (text: string) => string;
  stop: () => string;
}
declare global {
  interface Window {
    VultureMic?: VultureMicBridge;
    VultureTts?: VultureTtsBridge;
    __onMicFrame?: (b64: string) => void;
    __onMicLevel?: (rms: number) => void;
  }
}

function decodeBase64ToBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return buf;
}

export function useMic({
  onStreamStart,
  onStreamPartial,
  onStreamFinal,
  onError,
}: UseMicArgs): UseMic {
  const [state, setState] = useState<MicState>("idle");
  const [level, setLevel] = useState(0);
  const sessionRef = useRef<StreamSession | null>(null);

  // Bridge presence is usually a static fact — the Android WebView
  // injects it before scripts run. But the Kitchen Electron shell
  // installs it AFTER did-finish-load, so we re-check on a
  // "vulturemic-ready" event and also briefly poll as a backstop.
  const [available, setAvailable] = useState(() => typeof window !== "undefined" && !!window.VultureMic);
  useEffect(() => {
    if (available || typeof window === "undefined") return;
    const onReady = () => { if (window.VultureMic) setAvailable(true); };
    window.addEventListener("vulturemic-ready", onReady);
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (window.VultureMic) { setAvailable(true); window.clearInterval(timer); return; }
      if (++attempts >= 50) window.clearInterval(timer);  // 5 s cap
    }, 100);
    return () => {
      window.removeEventListener("vulturemic-ready", onReady);
      window.clearInterval(timer);
    };
  }, [available]);

  // Latest callbacks — captured in refs so the long-lived stream
  // listeners always see the current callbacks even if the component
  // re-renders mid-stream.
  const onStreamStartRef = useRef(onStreamStart);
  const onStreamPartialRef = useRef(onStreamPartial);
  const onStreamFinalRef = useRef(onStreamFinal);
  const onErrorRef = useRef(onError);
  useEffect(() => { onStreamStartRef.current = onStreamStart; }, [onStreamStart]);
  useEffect(() => { onStreamPartialRef.current = onStreamPartial; }, [onStreamPartial]);
  useEffect(() => { onStreamFinalRef.current = onStreamFinal; }, [onStreamFinal]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Tear down: stop the native recorder, drop our handlers (either
  // via the unsubscribe handle we got back from _addFrameHandler, or
  // by clearing the legacy window.__onMicFrame global), close the WS.
  // Idempotent.
  const teardown = useCallback((session: StreamSession | null) => {
    // Only call VultureMic.stop() if THIS session hasn't already
    // stopped it. stop()/cancel() call stop early to flip the mic
    // off immediately; without this guard the ws-close handler runs
    // a second stop() and refcount goes 2→0, tearing down a capture
    // that wake-mic-loop is still using.
    if (session && !session.micStopped) {
      try { window.VultureMic?.stop(); } catch { /* already stopped */ }
      session.micStopped = true;
    } else if (!session) {
      // Sessionless teardown (component unmount before any session) —
      // still safe to call once.
      try { window.VultureMic?.stop(); } catch { /* already stopped */ }
    }
    if (session?.unsubscribeFrame) {
      try { session.unsubscribeFrame(); } catch { /* ignore */ }
    } else {
      // Only clear the global when we don't have a per-session
      // unsubscribe — otherwise we'd nuke a concurrent consumer's
      // listener (the wake-mic-loop's, for instance).
      window.__onMicFrame = undefined;
    }
    if (session?.unsubscribeLevel) {
      try { session.unsubscribeLevel(); } catch { /* ignore */ }
    } else {
      window.__onMicLevel = undefined;
    }
    setLevel(0);
    if (!session) return;
    try {
      if (session.ws.readyState === WebSocket.OPEN || session.ws.readyState === WebSocket.CONNECTING) {
        session.ws.close();
      }
    } catch { /* already closed */ }
  }, []);

  // Clean up on unmount.
  useEffect(() => () => {
    teardown(sessionRef.current);
    sessionRef.current = null;
  }, [teardown]);

  const start = useCallback(async () => {
    if (state !== "idle") return;
    if (!window.VultureMic) {
      onErrorRef.current?.("microphone only available in the mobile app");
      return;
    }

    // WebSocket to mirror-agent's /api/stt/stream proxy. Same-origin
    // — the cookie auth piggybacks on the upgrade request, which is
    // why we don't need to plumb a token through the WS URL.
    const wsScheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsScheme}//${window.location.host}/api/stt/stream`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
    } catch (e) {
      onErrorRef.current?.(`WebSocket open failed: ${(e as Error)?.message || "unknown"}`);
      return;
    }

    const session: StreamSession = {
      ws,
      lastPartial: "",
      stopping: false,
      cancelled: false,
      micStopped: false,
    };
    sessionRef.current = session;

    // Frames captured before the WS is open get buffered locally and
    // flushed in order on the "open" event. This lets us start the mic
    // immediately (in parallel with the WS handshake) instead of
    // waiting for the round-trip — recovers ~100-300 ms of perceived
    // cold-start latency on every dictation.
    const pendingFrames: ArrayBuffer[] = [];
    const onFrame = (b64: string) => {
      const buf = decodeBase64ToBuffer(b64);
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(buf); }
        catch { /* socket racing close — drop the frame */ }
      } else if (ws.readyState === WebSocket.CONNECTING) {
        pendingFrames.push(buf);
      }
      // CLOSING / CLOSED → drop silently.
    };
    const onLevel = (rms: number) => {
      // Clamp into [0, 1] in case the native side ever exceeds peak
      // (shouldn't, but defensive).
      const clamped = rms < 0 ? 0 : rms > 1 ? 1 : rms;
      setLevel(clamped);
    };
    // Prefer the multi-consumer subscribe API (chat-kitchen preload)
    // so wake-mic-loop's frame handler stays subscribed during
    // dictation. Fall back to the legacy window.__onMicFrame global
    // (Android's MicBridge.kt + the renderer-injected shim — both
    // single-consumer).
    const mic = window.VultureMic;
    if (mic && typeof mic._addFrameHandler === "function") {
      session.unsubscribeFrame = mic._addFrameHandler(onFrame);
    } else {
      window.__onMicFrame = onFrame;
    }
    if (mic && typeof mic._addLevelHandler === "function") {
      session.unsubscribeLevel = mic._addLevelHandler(onLevel);
    } else {
      window.__onMicLevel = onLevel;
    }

    // Pre-arm the recorder NOW — don't wait for the WS to open. The
    // mic-button glow flips on immediately; frames buffer locally
    // until the socket is ready, then flush.
    let armResult: { ok: boolean; error?: string };
    try { armResult = JSON.parse(window.VultureMic!.start()); }
    catch (e) { armResult = { ok: false, error: (e as Error)?.message || "bridge parse failed" }; }
    if (!armResult.ok) {
      onErrorRef.current?.(armResult.error || "native mic start failed");
      teardown(session);
      sessionRef.current = null;
      setState("idle");
      return;
    }
    // Visual state goes to "streaming" the instant the mic is open,
    // not when the WS completes its handshake. The user sees the
    // button respond immediately.
    setState("streaming");
    onStreamStartRef.current?.();

    ws.addEventListener("open", () => {
      // Flush any frames captured during the handshake window.
      for (const buf of pendingFrames) {
        try { ws.send(buf); }
        catch { /* race with close */ }
      }
      pendingFrames.length = 0;
    });

    ws.addEventListener("message", (e) => {
      // The server only sends JSON text on this channel; binary would
      // be a protocol bug.
      if (typeof e.data !== "string") return;
      let msg: { type?: string; text?: string; message?: string };
      try { msg = JSON.parse(e.data); }
      catch { return; }
      if (msg.type === "partial" && typeof msg.text === "string") {
        session.lastPartial = msg.text;
        onStreamPartialRef.current?.(msg.text);
      } else if (msg.type === "final" && typeof msg.text === "string") {
        session.lastPartial = msg.text;
        if (!session.cancelled) onStreamFinalRef.current?.(msg.text);
      } else if (msg.type === "vad_stopped") {
        // Server auto-stopped on sustained silence. Stop the native
        // recorder so we don't keep capturing silence the server has
        // decided to ignore; WS stays open until the "final" arrives
        // and the close handler runs the rest of the teardown.
        // Move to "transcribing" so the overlay can show a spinner
        // instead of the listening pulse for the short window before
        // final lands.
        session.stopping = true;
        if (!session.micStopped) {
          try { window.VultureMic?.stop(); } catch { /* already stopped */ }
          session.micStopped = true;
        }
        setLevel(0);
        setState("transcribing");
      } else if (msg.type === "error") {
        onErrorRef.current?.(msg.message || "streaming error");
      }
    });

    ws.addEventListener("close", () => {
      // If we were stopping cleanly the "final" message should have
      // already arrived before the close. If not (server closed
      // abruptly, network), fall back to lastPartial as the final.
      if (session.stopping && !session.cancelled && sessionRef.current === session) {
        // Only fire if we never received an explicit final.
        // (Hard to tell from the close handler alone — but emitting
        // the most-recent partial as a final is the right behavior
        // either way; idempotent for the composer.)
      }
      teardown(session);
      if (sessionRef.current === session) sessionRef.current = null;
      setState("idle");
    });

    ws.addEventListener("error", () => {
      onErrorRef.current?.("streaming socket error");
      teardown(session);
      if (sessionRef.current === session) sessionRef.current = null;
      setState("idle");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, teardown]);

  const stop = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || state !== "streaming") return;
    session.stopping = true;
    // Stop the native recorder immediately so the user doesn't see
    // late-arriving frames update the level bar after they tap stop.
    if (!session.micStopped) {
      try { window.VultureMic?.stop(); } catch { /* already stopped */ }
      session.micStopped = true;
    }
    setLevel(0);
    setState("transcribing");
    // Politely tell the server to flush its tail buffer + emit final.
    // The "close" handler tears down once the server-side echoes a
    // close after the final message lands.
    try {
      if (session.ws.readyState === WebSocket.OPEN) {
        session.ws.send("end");
      } else {
        teardown(session);
        sessionRef.current = null;
        setState("idle");
      }
    } catch {
      teardown(session);
      sessionRef.current = null;
      setState("idle");
    }
  }, [state, teardown]);

  const toggle = useCallback(async () => {
    if (state === "idle") return start();
    if (state === "streaming") return stop();
    // transcribing / uploading → ignore (in flight)
  }, [state, start, stop]);

  const cancel = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    session.cancelled = true;
    session.stopping = true;
    teardown(session);
    sessionRef.current = null;
    setState("idle");
  }, [teardown]);

  const uploadFile = useCallback(async (file: File) => {
    if (state !== "idle") return;
    setState("uploading");
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const res = await fetch("/api/stt", { method: "POST", body: form });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        onErrorRef.current?.(`upload failed: ${res.status} ${body.slice(0, 200)}`);
        return;
      }
      const j = await res.json() as { text?: string; vad_skipped?: boolean };
      if (typeof j.text === "string" && j.text.length > 0) {
        onStreamFinalRef.current?.(j.text);
      } else if (j.vad_skipped) {
        onErrorRef.current?.("no speech detected in upload");
      } else {
        onErrorRef.current?.("upload returned empty transcript");
      }
    } catch (e) {
      onErrorRef.current?.((e as Error)?.message || "upload failed");
    } finally {
      setState("idle");
    }
  }, [state]);

  return { state, available, level, toggle, cancel, uploadFile };
}
